import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Plus } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'

const ALLOWED_EMOJI = ['❤️', '👍', '😂', '😮', '😢', '🙏'] as const

type Props = {
  postId: string
  reactionsSummary: Record<string, number>
  currentUserReaction: string | null
  canReact: boolean
}

export default function ReactionBar({
  postId,
  reactionsSummary,
  currentUserReaction,
  canReact,
}: Props) {
  const [open, setOpen] = useState(false)
  const visibleEmoji = ALLOWED_EMOJI.filter((e) => (reactionsSummary[e] ?? 0) > 0)
  const hasAnyChips = visibleEmoji.length > 0

  function react(emoji: string) {
    if (emoji === currentUserReaction) {
      router.delete(`/posts/${postId}/reaction`, { preserveScroll: true })
    } else {
      router.post(`/posts/${postId}/reaction`, { emoji }, { preserveScroll: true })
    }
    setOpen(false)
  }

  if (!hasAnyChips && !canReact) return null

  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5">
      {visibleEmoji.map((emoji) => {
        const isMine = emoji === currentUserReaction
        return (
          <Button
            key={emoji}
            type="button"
            variant="outline"
            size="sm"
            disabled={!canReact}
            onClick={() => canReact && react(emoji)}
            className={`h-7 gap-1 rounded-full px-2 text-xs ${
              isMine ? 'border-primary/30 bg-primary/10' : ''
            }`}
            aria-pressed={isMine}
            aria-label={`React with ${emoji}${isMine ? ' (your reaction — tap to remove)' : ''}`}
          >
            <span aria-hidden>{emoji}</span>
            <span className="font-medium tabular-nums">{reactionsSummary[emoji]}</span>
          </Button>
        )
      })}
      {canReact && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 gap-1 rounded-full px-2 text-xs text-muted-foreground"
              aria-label="Add reaction"
            >
              <Plus className="size-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-1.5" align="start">
            <div className="flex gap-0.5">
              {ALLOWED_EMOJI.map((emoji) => (
                <Button
                  key={emoji}
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => react(emoji)}
                  className={`h-9 w-9 rounded-md p-0 text-lg ${
                    emoji === currentUserReaction ? 'bg-primary/10' : ''
                  }`}
                  aria-label={`React with ${emoji}`}
                >
                  {emoji}
                </Button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}
